import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ecommerceApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

/**
 * Reviews moderation — FR-ECOM-05
 * Approve / reject product reviews.
 */
export function ReviewsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('pending');
  const [rejectingReview, setRejectingReview] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['ecom-reviews', statusFilter],
    queryFn: () => ecommerceApi.listReviews({ status: statusFilter || undefined, limit: 200 }),
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.approveReview(id),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['ecom-reviews'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => ecommerceApi.rejectReview(id, reason),
    onSuccess: () => {
      toast.success('Rejected');
      qc.invalidateQueries({ queryKey: ['ecom-reviews'] });
      setRejectingReview(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = useMemo(() => {
    const rows = q.data ?? [];
    const count = { total: rows.length, pending: 0, approved: 0, rejected: 0, avgRating: 0 };
    let ratingSum = 0;
    for (const r of rows) {
      if (r.status === 'pending') count.pending++;
      else if (r.status === 'approved') count.approved++;
      else if (r.status === 'rejected') count.rejected++;
      ratingSum += parseFloat(r.rating) || 0;
    }
    count.avgRating = rows.length > 0 ? ratingSum / rows.length : 0;
    return count;
  }, [q.data]);

  const STATUS_OPTIONS: Array<{ value: 'pending' | 'approved' | 'rejected' | ''; label: string }> = [
    { value: 'pending',  label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: '',         label: 'All' },
  ];

  return (
    <div className="px-8 py-10 max-w-5xl mx-auto">
      <PageHeader
        eyebrow="Online store"
        title="Reviews"
        subtitle="Moderate customer reviews before they appear on your public storefronts."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Pending" value={kpis.pending}
          accent={kpis.pending > 0 ? 'amber' : 'default'} />
        <KpiTile label="Approved" value={kpis.approved} accent="sage" />
        <KpiTile label="Avg rating"
          value={kpis.total > 0 ? `${kpis.avgRating.toFixed(1)} ★` : '—'}
          accent={kpis.avgRating >= 4 ? 'sage' : 'default'} />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : !q.data?.length ? (
        <div className="card">
          <EmptyState
            message={statusFilter === 'pending' ? 'No pending reviews' : 'No reviews'}
            hint={statusFilter === 'pending'
              ? 'Every submitted review has been moderated. Great work!'
              : 'Try a different filter.'}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {(q.data ?? []).map((r: any) => (
            <div key={r.id} className="card p-5">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  {/* Star rating */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars rating={parseInt(r.rating) || 0} />
                    {r.title && (
                      <span className="font-semibold text-ink-900">{r.title}</span>
                    )}
                    <ReviewStatusBadge status={r.status} />
                    {r.verified_purchase && (
                      <span className="badge bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200 text-2xs">
                        Verified purchase
                      </span>
                    )}
                  </div>
                  {/* Product + reviewer */}
                  <div className="text-xs text-ink-500 mt-2">
                    <span className="font-semibold text-ink-700">{r.product_name}</span>
                    {r.sku && <span className="font-mono text-ink-400 ml-2">{r.sku}</span>}
                    <span className="text-ink-400 mx-1.5">·</span>
                    <span>{r.storefront_name}</span>
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    by <span className="font-medium text-ink-700">{r.customer_name ?? r.reviewer_name ?? 'Guest'}</span>
                    {r.reviewer_email && <span className="text-ink-400"> · {r.reviewer_email}</span>}
                    <span className="text-ink-400 mx-1.5">·</span>
                    <span>{new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </div>

                {r.status === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      className="text-xs font-medium text-ink-500 hover:text-danger transition-colors"
                      disabled={rejectMut.isPending}
                      onClick={() => setRejectingReview(r)}
                    >
                      Reject
                    </button>
                    <button
                      className="text-xs font-semibold text-sage-700 hover:text-sage-800 transition-colors"
                      disabled={approveMut.isPending}
                      onClick={() => approveMut.mutate(r.id)}
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>

              {r.body && (
                <div className="text-sm text-ink-700 mt-3 whitespace-pre-line leading-relaxed">
                  {r.body}
                </div>
              )}

              {r.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mt-3">
                  <div className="font-semibold">Rejected</div>
                  <div className="mt-0.5">{r.rejection_reason}</div>
                  {r.moderator_name && <div className="mt-1 text-red-600">by {r.moderator_name}</div>}
                </div>
              )}

              {r.moderated_at && r.status === 'approved' && r.moderator_name && (
                <div className="text-2xs text-sage-600 mt-3">
                  Approved by {r.moderator_name} on {new Date(r.moderated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rejectingReview && (
        <RejectReviewDialog
          review={rejectingReview}
          loading={rejectMut.isPending}
          onClose={() => setRejectingReview(null)}
          onConfirm={(reason) => rejectMut.mutate({ id: rejectingReview.id, reason })}
        />
      )}
    </div>
  );
}

function RejectReviewDialog({ review, loading, onClose, onConfirm }: {
  review: any; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Commerce · Reviews</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject review?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          The review will be hidden from the storefront. Your reason is visible to moderators.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Rejection reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Contains offensive language, spam, or unverified purchase" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Rejecting…' : 'Reject review'}
        </button>
      </div>
    </Modal>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center text-sage-500 text-sm">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={i < rating ? '' : 'text-ink-200'}>★</span>
      ))}
    </div>
  );
}

function ReviewStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'pending'  ? 'warning' :
    status === 'approved' ? 'sage' :
    status === 'rejected' ? 'danger' :
                            'neutral';
  return <StatusBadge status={status} tone={tone as any} />;
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
